/**
 * @fileoverview Displays user notifications list
 * @description Renders notifications with all/unread filter; fetches data if logged in
 * @active Client component [CONFIRMED]
 *
 * @see {@link src/components/notification/NotificationMenu.tsx} - Parent component in header
 * @see {@link src/services/notifications.service.ts} - Notification fetch service (RTK Query)
 * @see {@link src/store/notifications/notifications.selectors.ts} - Notification data selectors (Redux)
 * @see {@link src/providers/supabaseAuthProvider.client.tsx} - Auth state check hook
 * @see {@link src/components/notification/NotificationCard.tsx} - Renders individual notification
 * @see {@link src/components/notification/NotificationsHeader.tsx} - All/Unread filter header
 * @see {@link src/components/notification/NoNotifications.tsx} - Empty state component
 */

import { memo, useEffect, useState } from "react";

import { Flex } from "@chakra-ui/react";
import { usePathname } from "next/navigation";
import { useSelector } from "react-redux";

import { useSupabaseAuth } from "@/providers/supabaseAuthProvider.client"; // Import the new hook
import {
  useLazyGetNotificationsQuery,
  useLazyGetUnreadNotificationsQuery,
} from "@/services/notifications.service";
import {
  getNotificationsData,
  getUnreadNotificationsData,
} from "@/store/notifications/notifications.selectors";

import { BlueTextBoxArrow } from "../ui/box/BlueTextBoxArrow";

import { NoNotifications } from "./NoNotifications";
import NotificationCard from "./NotificationCard";
import NotificationsHeader from "./NotificationsHeader";

/**
 * Props for NotificationsList component
 */
interface NotificationsListProps {
  /** If true, adjusts styles for rendering within header menu dropdown */
  isHeaderMenu?: boolean;
}

/**
 * Renders the list of notifications with filtering options
 *
 * @component NotificationsList
 * @param {NotificationsListProps} props - Component props
 * @returns {JSX.Element} Rendered notifications list
 */
const NotificationsList = ({
  isHeaderMenu = false,
}: NotificationsListProps): JSX.Element => {
  const pathname = usePathname();
  const [getNotifications] = useLazyGetNotificationsQuery();
  const [getUnreadNotifications] = useLazyGetUnreadNotificationsQuery();

  const allNotifications = useSelector(getNotificationsData());
  const notificationsUnread = useSelector(getUnreadNotificationsData());
  const { user, isLoading: isAuthLoading } = useSupabaseAuth(); // Use the new hook
  const userIsVerify = !!user && !isAuthLoading; // Derive userIsVerify
  const [isUnread, setIsUnread] = useState(false);

  const allNotificationHandler = () => {
    setIsUnread(false);
  };

  const unreadNotificationHandler = () => {
    setIsUnread(true);
  };

  useEffect(() => {
    // Fetch notifications based on login status and filter
    const timer = setTimeout(() => {
      if (userIsVerify) {
        if (!isUnread) {
          getNotifications({}); // Pass empty object argument
        } else {
          getUnreadNotifications({}); // Pass empty object argument
        }
      }
    }, 10); // Short delay

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIsVerify, isUnread]);

  return (
    <Flex
      flexDir={"column"}
      p={isHeaderMenu ? "0" : "20px"}
      rowGap={"12px"}
      w={"100%"}
      maxW={"507px"}
      bg={"white"}
      borderRadius={"4px"}
      mx={{ base: "auto" }}
      mb={isHeaderMenu ? "0" : "80px"}
    >
      {pathname === "/notifications" ? (
        <BlueTextBoxArrow
          {...{
            title: "Notifications",
            boxStyles: {
              ml: "-20px",
            },
          }}
        />
      ) : null}
      <NotificationsHeader
        {...{
          allNotificationHandler,
          isUnread,
          unreadNotificationHandler,
          isHeaderMenu,
        }}
      />
      {!isUnread ? (
        allNotifications?.length ? (
          <>
            {allNotifications?.map((item) => (
              <NotificationCard key={item?.id} {...{ itemEl: item }} />
            ))}
          </>
        ) : (
          <NoNotifications
            {...{ isUnread, getNotifications, getUnreadNotifications }}
          />
        )
      ) : notificationsUnread?.length ? (
        <>
          {notificationsUnread?.map((item) => (
            <NotificationCard key={item?.id} {...{ itemEl: item }} />
          ))}
        </>
      ) : (
        <NoNotifications
          {...{ isUnread, getNotifications, getUnreadNotifications }}
        />
      )}
    </Flex>
  );
};

export default memo(NotificationsList);
